import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { EvolutionReseaux } from './EvolutionReseaux';
interface Ligne {
  organisation: string;
  province: string;
  domaine: string;
  fictive: boolean;
  incidents: number;
  confirmations_mesurees: number;
  retablissements_mesures: number;
  confirmation_heures: number | null;
  retablissement_heures: number | null;
}
export function DelaisReseaux({
  filtres,
  contexte,
}: {
  filtres: { province_id?: string; domaine?: string; inclure_fictives: string };
  contexte: string;
}) {
  const jour = new Date(Date.now() + 3600000).toISOString().slice(0, 10);
  const [debut, setDebut] = useState(
    new Date(Date.parse(jour) - 29 * 86400000).toISOString().slice(0, 10),
  );
  const [fin, setFin] = useState(jour);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const valide =
    !!debut && !!fin && fin >= debut && (Date.parse(fin) - Date.parse(debut)) / 86400000 <= 365;
  const parametres = { ...filtres, debut, fin };
  const q = useQuery({
    queryKey: ['delais-reseaux', contexte, parametres],
    queryFn: () =>
      requete<{ elements: Ligne[]; calcule_le: string; tronque: boolean }>(
        '/supervision/reseaux/delais',
        { parametres },
      ),
    enabled: valide,
  });
  const duree = (n: number | null) =>
    n === null ? 'Non mesuré' : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' h';
  return (
    <section aria-label="Délais de traitement">
      <h2>Délais de traitement</h2>
      <p>
        Incidents créés pendant la période, en heure de Libreville. Ces délais décrivent le
        traitement des dossiers.
      </p>
      <details>
        <summary>Méthode de calcul et limites</summary>
        <p>
          Confirmation : moyenne depuis l’enregistrement jusqu’à la première confirmation datée.
          Rétablissement : moyenne jusqu’au dernier rétablissement vérifié, uniquement pour les
          incidents actuellement rétablis ou clôturés. Un incident rouvert reste exclu du délai de
          rétablissement tant qu’il n’est pas de nouveau vérifié.
        </p>
        <p>
          Les effectifs mesurés sont indiqués séparément : une donnée manquante ne vaut pas zéro.
          Ces délais de traitement ne mesurent ni la durée réelle des coupures ni la disponibilité
          du service. Ils évoluent lorsque les dossiers avancent.
        </p>
      </details>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label>
          Créés à partir du{' '}
          <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
        </label>
        <label>
          Créés jusqu’au <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
        </label>
      </div>
      {!valide && <p role="alert">Choisissez une période de 1 à 366 jours dans le bon ordre.</p>}
      {(q.error || erreur) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {valide && q.isPending && <p>Calcul des délais…</p>}
      {valide && q.data && (
        <>
          <EvolutionReseaux parametres={parametres} contexte={contexte} />
          <p>
            Calculé le {new Date(q.data.calcule_le).toLocaleString('fr-FR')}. Les filtres province,
            service et démonstration du pilotage s’appliquent aussi aux délais.
          </p>
          {q.data.tronque && <p role="alert">Plus de 2 000 lignes : affinez les filtres.</p>}
          {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
            <button
              key={format}
              disabled={
                occupe ||
                q.isFetching ||
                q.data.tronque ||
                (format === 'pdf' && q.data.elements.length > 200)
              }
              onClick={async () => {
                setErreur('');
                setOccupe(true);
                try {
                  const r = await requete<Response>(
                    format === 'pdf'
                      ? '/supervision/reseaux/delais/rapport.pdf'
                      : '/supervision/reseaux/delais/export',
                    {
                      parametres: { ...parametres, ...(format === 'pdf' ? {} : { format }) },
                      brut: true,
                    },
                  );
                  const url = URL.createObjectURL(await r.blob());
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `delais-reseaux.${format}`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (e) {
                  setErreur(messageErreur(e));
                } finally {
                  setOccupe(false);
                }
              }}
            >
              Exporter les délais {format === 'csv' ? 'CSV' : format === 'pdf' ? 'PDF' : 'Excel'}
            </button>
          ))}
          {q.data.elements.length > 200 && (
            <p>Le PDF des délais est limité à 200 lignes. Affinez les filtres ou utilisez Excel.</p>
          )}
          {occupe && <p role="status">Préparation du téléchargement des délais…</p>}
          {q.data.elements.length === 0 ? (
            <p>Aucun incident enregistré sur cette période dans votre périmètre.</p>
          ) : (
            <div
              role="region"
              aria-label="Tableau des délais"
              tabIndex={0}
              style={{ overflowX: 'auto' }}
            >
              <table>
                <caption>Délais moyens et nombre d’incidents effectivement mesurés</caption>
                <thead>
                  <tr>
                    {[
                      'Organisation',
                      'Province',
                      'Service',
                      'Incidents créés',
                      'Confirmations mesurées',
                      'Délai moyen de confirmation',
                      'Rétablissements mesurés',
                      'Délai moyen de rétablissement',
                    ].map((t) => (
                      <th key={t} scope="col">
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data.elements.map((l, i) => (
                    <tr key={i}>
                      <td>
                        {l.organisation}
                        {l.fictive ? ' (démonstration)' : ''}
                      </td>
                      <td>{l.province}</td>
                      <td>{l.domaine === 'WATER' ? 'Eau' : 'Électricité'}</td>
                      <td>{l.incidents}</td>
                      <td>{l.confirmations_mesurees}</td>
                      <td>{duree(l.confirmation_heures)}</td>
                      <td>{l.retablissements_mesures}</td>
                      <td>{duree(l.retablissement_heures)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
